import React, { useState, useEffect } from 'react';
import { useApi } from '../../services/api';
import AddressFields from '../AddressFields';

const WizardStepAddress = ({
  onNext,
  onBack,
  wizardData,
  setWizardData
}) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAddressId, setSelectedAddressId] = useState(
    wizardData.selected_address_id || null
  );
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [newAddressData, setNewAddressData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
    is_default: false
  });

  const api = useApi();

  // Fetch addresses on mount
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        setLoading(true);
        const response = await api.getUserAddresses();
        const addressList = response.data?.addresses || response.data || [];
        setAddresses(addressList);

        // Auto-select default address if none selected
        if (!selectedAddressId && addressList.length > 0) {
          const defaultAddress = addressList.find(addr => addr.is_default) || addressList[0];
          setSelectedAddressId(defaultAddress.id);
          setWizardData(prev => ({
            ...prev,
            selected_address_id: defaultAddress.id
          }));
        } else if (addressList.length === 0) {
          // No addresses - show form
          setShowAddressForm(true);
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
        setAddresses([]);
        setShowAddressForm(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, []);

  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);
    setShowAddressForm(addressId === 'new_address');
    setWizardData(prev => ({
      ...prev,
      selected_address_id: addressId === 'new_address' ? null : addressId
    }));
  };

  const handleContinue = () => {
    if (!selectedAddressId || selectedAddressId === 'new_address') {
      return;
    }
    onNext();
  };

  const handleNewAddressSubmit = async () => {
    setFormError('');

    // Validate required fields
    if (!newAddressData.first_name || !newAddressData.last_name ||
        !newAddressData.address_line_1 || !newAddressData.city ||
        !newAddressData.state || !newAddressData.zip_code) {
      setFormError('Please fill in all required fields');
      return;
    }

    setFormLoading(true);
    try {
      const response = await api.createUserAddress({
        ...newAddressData,
        country: 'US'
      });

      const newAddress = response.data?.address || response.data;

      // Refetch addresses to get updated list
      const addressResponse = await api.getUserAddresses();
      const addressList = addressResponse.data?.addresses || addressResponse.data || [];
      setAddresses(addressList);

      // Select the new address
      const createdAddressId = newAddress?.id || addressList[addressList.length - 1]?.id;
      setSelectedAddressId(createdAddressId);
      setWizardData(prev => ({
        ...prev,
        selected_address_id: createdAddressId
      }));

      setShowAddressForm(false);

      // Continue to next step
      onNext();
    } catch (error) {
      console.error('Error creating address:', error);
      setFormError(error.response?.data?.error || 'Failed to save address. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormCancel = () => {
    if (addresses.length > 0) {
      // Reset to first/default address
      const defaultAddress = addresses.find(addr => addr.is_default) || addresses[0];
      setSelectedAddressId(defaultAddress.id);
      setShowAddressForm(false);
      setWizardData(prev => ({
        ...prev,
        selected_address_id: defaultAddress.id
      }));
    }
  };

  const formatAddress = (addr) => {
    const parts = [addr.address_line_1];
    if (addr.address_line_2) parts.push(addr.address_line_2);
    parts.push(`${addr.city}, ${addr.state} ${addr.zip_code}`);
    return parts;
  };

  if (loading) {
    return (
      <div className="min-h-[450px] bg-gradient-to-br from-gray-50 to-gray-200 py-3 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading addresses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[450px] bg-gradient-to-br from-gray-50 to-gray-200 py-3 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Content Section */}
        <div className="flex justify-center">
          <div className="w-full max-w-2xl">
            <div className="space-y-4">
              <h6 className="mb-3 text-lg font-semibold text-gray-800">
                Where should we ship your {wizardData.metal_type}?
              </h6>
              <p className="text-sm text-gray-600 mb-4">
                Select a shipping address for when your goal is complete
              </p>

              {addresses.length > 0 && !showAddressForm && (
                <>
                  {/* Sort addresses with default first */}
                  {[...addresses]
                    .sort((a, b) => {
                      if (a.is_default && !b.is_default) return -1;
                      if (!a.is_default && b.is_default) return 1;
                      return 0;
                    })
                    .map((addr) => (
                      <div
                        key={addr.id}
                        className={`
                          bg-white rounded-xl p-4 cursor-pointer border-2 transition-all
                          ${selectedAddressId === addr.id
                            ? 'border-blue-500 shadow-lg ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-blue-500'
                          }
                        `}
                        onClick={() => handleAddressSelect(addr.id)}
                      >
                        <div className="flex items-start">
                          <div className="mr-3 mt-1">
                            <input
                              type="radio"
                              name="address"
                              checked={selectedAddressId === addr.id}
                              onChange={() => handleAddressSelect(addr.id)}
                              className="w-4 h-4"
                            />
                          </div>
                          <div className="flex-grow">
                            <div className="flex items-center gap-2 mb-1">
                              <strong className="text-gray-800">
                                {addr.first_name} {addr.last_name}
                              </strong>
                              {addr.is_default && (
                                <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded">
                                  Default
                                </span>
                              )}
                            </div>
                            {addr.company && (
                              <div className="text-gray-600 text-sm">{addr.company}</div>
                            )}
                            {formatAddress(addr).map((line, idx) => (
                              <div key={idx} className="text-gray-600 text-sm">{line}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}

                  <div className="border-t border-gray-300 my-4"></div>
                </>
              )}

              {/* Add new address option */}
              {!showAddressForm && (
                <div
                  className={`
                    bg-white rounded-xl p-4 cursor-pointer transition-all
                    ${selectedAddressId === 'new_address'
                      ? 'border-2 border-blue-500 shadow-lg ring-2 ring-blue-200'
                      : 'border-2 border-dashed border-gray-300 hover:border-blue-500'
                    }
                  `}
                  onClick={() => handleAddressSelect('new_address')}
                >
                  <div className="flex items-center">
                    <div className="mr-3">
                      <input
                        type="radio"
                        name="address"
                        checked={selectedAddressId === 'new_address'}
                        onChange={() => handleAddressSelect('new_address')}
                        className="w-4 h-4"
                      />
                    </div>
                    <div className="mr-3 text-2xl">➕</div>
                    <div className="flex-grow">
                      <strong className="text-gray-800">Add new address</strong>
                      <br />
                      <small className="text-gray-600">
                        Add a new shipping address
                      </small>
                    </div>
                  </div>
                </div>
              )}

              {/* Inline Address Form */}
              {showAddressForm && (
                <div className="mt-4 animate-slideDown">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                    <h6 className="mb-4 text-lg font-semibold text-gray-800">
                      {addresses.length === 0 ? 'Add Your Shipping Address' : 'New Shipping Address'}
                    </h6>

                    {formError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                        {formError}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name *
                        </label>
                        <input
                          type="text"
                          value={newAddressData.first_name}
                          onChange={(e) => setNewAddressData(prev => ({ ...prev, first_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={formLoading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          value={newAddressData.last_name}
                          onChange={(e) => setNewAddressData(prev => ({ ...prev, last_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={formLoading}
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company (Optional)
                      </label>
                      <input
                        type="text"
                        value={newAddressData.company}
                        onChange={(e) => setNewAddressData(prev => ({ ...prev, company: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>

                    <AddressFields
                      addressData={newAddressData}
                      setAddressData={setNewAddressData}
                      disabled={formLoading}
                    />

                    <div className="flex gap-3 mt-6">
                      {addresses.length > 0 && (
                        <button
                          type="button"
                          onClick={handleFormCancel}
                          disabled={formLoading}
                          className="flex-1 bg-white text-gray-700 px-4 py-2 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleNewAddressSubmit}
                        disabled={formLoading}
                        className="flex-1 bg-black text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
                      >
                        {formLoading ? 'Saving...' : 'Save & Continue'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <div className="flex justify-between mt-4 max-w-2xl mx-auto">
          <button
            onClick={onBack}
            className="bg-white text-black px-6 py-2 rounded-full font-bold border-2 border-black hover:bg-gray-50 transition-colors"
          >
            <i className="bi bi-arrow-left mr-2"></i>
            Back
          </button>
          {!showAddressForm && (
            <button
              onClick={handleContinue}
              disabled={!selectedAddressId || selectedAddressId === 'new_address'}
              className={`px-6 py-2 rounded-full font-bold transition-colors min-w-[140px] ${
                selectedAddressId && selectedAddressId !== 'new_address'
                  ? 'bg-black text-white hover:bg-gray-800'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continue
              <i className="bi bi-arrow-right ml-2"></i>
            </button>
          )}
        </div>

        {!selectedAddressId && !showAddressForm && (
          <small className="text-red-600 mt-2 block text-center">Please select an address to continue</small>
        )}
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default WizardStepAddress;
